import clsx from 'clsx';
import { ComponentPropsWithoutRef, ElementType, memo, PropsWithChildren, ReactNode } from 'react';
import { LoadingIcon } from 'src/modules/common/icons/LoadingIcon';

type PolymorphicProps<E extends ElementType> = PropsWithChildren<ComponentPropsWithoutRef<E> & {
    as?: E;
}>

export type ButtonProps<T extends React.ElementType> = PolymorphicProps<T> & {
    kind?: 'primary' | 'secondary' | 'tertiary' | 'ghost' | 'danger';
    size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
    iconOnly?: boolean;
    fullWidth?: boolean;
    loading?: boolean;
    reversed?: boolean;
    disabled?: boolean;
    icon?: ReactNode;
    buttonRef?: (node: HTMLButtonElement | null) => void;
    children?: ReactNode;
}

function ButtonWithoutMemo<T extends ElementType = 'button'>({
    as,
    className = '',
    disabled = false,
    kind = 'primary',
    size = 'md',
    iconOnly = false,
    fullWidth = false,
    loading = false,
    reversed = false,
    icon,
    buttonRef,
    children,
    ...props
}: ButtonProps<T>) {
    const Component = as || 'button';

    return (
        <Component
            ref={buttonRef}
            className={clsx({
                'vss-button': true,
                'vss-button--full-width': fullWidth,
                'vss-button--reversed': reversed,
                [`vss-button--${kind}`]: true,
                [`vss-button--${kind}-disabled`]: disabled || loading,
                [`vss-button--${size}`]: true,
                [`vss-button--${size}-icon-only`]: iconOnly,
                [className]: className,
            })}
            disabled={disabled || loading}
            {...props}
        >
            {children}

            {loading ? <LoadingIcon className="vss-button__loading-icon"/> : icon}
        </Component>
    );
}

export const Button = memo(ButtonWithoutMemo) as typeof ButtonWithoutMemo;
